import React, { useState } from "react";
import { useLanguage } from "@/context/LanguageContext";
import { postContact } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Mail, Loader2, Send } from "lucide-react";

export const ContactSection = () => {
  const { t } = useLanguage();
  const [form, setForm] = useState({ name: "", email: "", subject: "", message: "" });
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await postContact(form);
      toast.success(t.contact.form.success);
      setForm({ name: "", email: "", subject: "", message: "" });
    } catch (err) {
      toast.error(t.contact.form.error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="contact" className="relative py-28 md:py-36 border-t border-white/5" data-testid="contact-section">
      <div className="max-w-5xl mx-auto px-6 md:px-12">
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          <div>
            <p className="text-xs uppercase tracking-[0.4em] text-bicenter-cyan font-mono">{t.contact.kicker}</p>
            <h2 className="mt-4 font-display text-4xl md:text-5xl font-light tracking-tight text-white leading-[1.05]">
              {t.contact.title}
            </h2>
            <p className="mt-6 text-zinc-400">{t.contact.subtitle}</p>

            <div className="mt-10 space-y-4">
              <a href="mailto:connect@bicenter-ai.com" className="flex items-center gap-3 text-zinc-300 hover:text-white transition">
                <span className="w-10 h-10 rounded-full bg-white/5 border border-white/10 flex items-center justify-center">
                  <Mail size={15} className="text-bicenter-cyan" />
                </span>
                connect@bicenter-ai.com
              </a>
              <a href="https://bicenter-ai.com" target="_blank" rel="noreferrer" className="flex items-center gap-3 text-zinc-300 hover:text-white transition">
                <span className="w-10 h-10 rounded-full bg-white/5 border border-white/10 flex items-center justify-center text-bicenter-violet">
                  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><path d="M2 12h20"/><path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z"/></svg>
                </span>
                bicenter-ai.com
              </a>
            </div>
          </div>

          <form onSubmit={submit} className="space-y-4 bg-[#0c0c0e] border border-white/10 rounded-3xl p-8" data-testid="contact-form">
            <div className="grid md:grid-cols-2 gap-3">
              <Field label={t.contact.form.name} required>
                <Input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="contact-input-name" className="bg-white/[0.03] border-white/10 text-white h-12" />
              </Field>
              <Field label={t.contact.form.email} required>
                <Input required type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="contact-input-email" className="bg-white/[0.03] border-white/10 text-white h-12" />
              </Field>
            </div>
            <Field label={t.contact.form.subject}>
              <Input value={form.subject} onChange={(e) => setForm({ ...form, subject: e.target.value })} data-testid="contact-input-subject" className="bg-white/[0.03] border-white/10 text-white h-12" />
            </Field>
            <Field label={t.contact.form.message} required>
              <Textarea required rows={4} value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} data-testid="contact-input-message" className="bg-white/[0.03] border-white/10 text-white" />
            </Field>
            <Button
              type="submit"
              disabled={loading}
              data-testid="contact-submit"
              className="w-full h-12 bg-white text-black font-medium hover:bg-zinc-200"
            >
              {loading ? (
                <span className="inline-flex items-center gap-2">
                  <Loader2 size={14} className="animate-spin" /> {t.common.loading}
                </span>
              ) : (
                <span className="inline-flex items-center gap-2">
                  <Send size={14} /> {t.contact.form.submit}
                </span>
              )}
            </Button>
          </form>
        </div>
      </div>
    </section>
  );
};

const Field = ({ label, required, children }) => (
  <div className="space-y-2">
    <Label className="text-xs uppercase tracking-[0.2em] text-zinc-400">
      {label} {required && <span className="text-bicenter-magenta">*</span>}
    </Label>
    {children}
  </div>
);
